// ─────────────────────────────────────────────────────────────
// ZIFEK — Intégration FeexPay (API v2).
//
// FeexPay est un agrégateur de Mobile Money multi-pays
// (Bénin, Togo, Côte d'Ivoire, Sénégal, Burkina Faso, Congo…).
//
// ⚠️ Point clé de l'API v2 : l'URL dépend de l'OPÉRATEUR.
//   POST https://api-v2.feexpay.me/api/transactions/public/requesttopay/{operateur}
//   Authorization: Bearer <clé API>
//
// Le PAYS est déterminé par le « shop » (boutique FeexPay) configuré côté
// compte ; l'identifiant d'URL, lui, correspond à l'opérateur (mtn, moov,
// celtiis, coris, orange, wave, free, tmoney…). Comme les slugs peuvent
// différer selon les comptes, chaque identifiant est éditable côté superadmin.
//
// C'est un « request to pay » (push USSD) : le client valide sur son
// téléphone, il n'y a pas forcément de page de redirection. Le webhook
// (feexpay-callback) confirme le paiement.
// ─────────────────────────────────────────────────────────────

export const FEEXPAY_DEFAULT_API_URL = 'https://api-v2.feexpay.me';
/** @deprecated L'hôte sandbox ne résout plus en DNS : conservé pour compatibilité, jamais utilisé. */
export const FEEXPAY_DEFAULT_SANDBOX_API_URL = 'https://api-v2.feexpay.me';
export const FEEXPAY_MIN_AMOUNT = 100;
export const FEEXPAY_MAX_AMOUNT = 2000000;
export const FEEXPAY_CURRENCY = 'XOF';

export interface FeexPayOperator {
  /** Identifiant utilisé dans l'URL (ex. « mtn »). */
  slug: string;
  label: string;
}

export interface FeexPayCountry {
  code: string;
  label: string;
  currency: string;
  /** Indicatif téléphonique international (sans « + »). */
  callingCode: string;
  operators: FeexPayOperator[];
}

/**
 * Catalogue des pays et opérateurs FeexPay.
 * Les identifiants (slug) sont ceux documentés pour le Bénin (« mtn »…) ;
 * les autres sont des valeurs par défaut éditables dans les réglages.
 */
export const FEEXPAY_COUNTRIES: FeexPayCountry[] = [
  {
    code: 'BJ',
    label: 'Bénin',
    currency: 'XOF',
    callingCode: '229',
    operators: [
      { slug: 'mtn', label: 'MTN Bénin' },
      { slug: 'moov', label: 'Moov Bénin' },
      { slug: 'celtiis', label: 'Celtiis Cash' },
      { slug: 'coris', label: 'Coris Money' },
    ],
  },
  {
    code: 'TG',
    label: 'Togo',
    currency: 'XOF',
    callingCode: '228',
    operators: [
      { slug: 'moov', label: 'Moov Togo' },
      { slug: 'tmoney', label: 'T-Money (Togocom)' },
      { slug: 'flooz', label: 'Flooz' },
    ],
  },
  {
    code: 'CI',
    label: "Côte d'Ivoire",
    currency: 'XOF',
    callingCode: '225',
    operators: [
      { slug: 'mtn', label: 'MTN Côte d’Ivoire' },
      { slug: 'moov', label: 'Moov Côte d’Ivoire' },
      { slug: 'orange', label: 'Orange Côte d’Ivoire' },
      { slug: 'wave', label: 'Wave Côte d’Ivoire' },
    ],
  },
  {
    code: 'SN',
    label: 'Sénégal',
    currency: 'XOF',
    callingCode: '221',
    operators: [
      { slug: 'orange', label: 'Orange Sénégal' },
      { slug: 'wave', label: 'Wave Sénégal' },
      { slug: 'free', label: 'Free Money' },
    ],
  },
  {
    code: 'BF',
    label: 'Burkina Faso',
    currency: 'XOF',
    callingCode: '226',
    operators: [
      { slug: 'moov', label: 'Moov Burkina' },
      { slug: 'orange', label: 'Orange Burkina' },
    ],
  },
  {
    code: 'CG',
    label: 'Congo Brazzaville',
    currency: 'XAF',
    callingCode: '242',
    operators: [
      { slug: 'mtn', label: 'MTN Congo' },
      { slug: 'airtel', label: 'Airtel Congo' },
    ],
  },
];

/** Surcharge de configuration stockée dans `payment_providers.config.networks`. */
export interface FeexPayNetworkOverride {
  country: string;
  slug: string;
  label?: string;
  enabled?: boolean;
}

export interface FeexPayConfig {
  api_key?: string;
  api_url?: string;
  callback_url?: string;
  /** Pays par défaut de la boutique. */
  country?: string;
  /** Identifiant d'opérateur par défaut (slug d'URL). */
  network?: string;
  /** Taux de conversion vers le XOF pour les montants dans une autre devise. */
  xof_rate?: number | string;
  /** Taux de conversion par devise (1 unité → XOF). Prioritaire sur `xof_rate`. */
  xof_rates?: Record<string, number | string>;
  networks?: FeexPayNetworkOverride[];
  /** Le paiement par carte bancaire (Card collection) est-il proposé ? */
  card_enabled?: boolean;
  /** URL complète de collecte carte (facultatif : sinon URL par défaut déduite). */
  card_url?: string;
}

export interface FeexPayNetworkOption extends FeexPayOperator {
  country: string;
  countryLabel: string;
  enabled: boolean;
  /** Vrai si l'entrée provient du catalogue par défaut. */
  fromCatalog: boolean;
}

export function getFeexPayCountry(code: string | undefined | null): FeexPayCountry | undefined {
  if (!code) return undefined;
  return FEEXPAY_COUNTRIES.find((c) => c.code === code);
}

export function getFeexPayCallingCode(code: string | undefined | null): string {
  return getFeexPayCountry(code)?.callingCode || '';
}

/**
 * Liste des opérateurs disponibles.
 * Si le superadmin a enregistré sa propre liste (`config.networks`), elle fait
 * foi — sinon on retombe sur le catalogue par défaut (tous activés).
 */
export function resolveFeexPayNetworks(config?: FeexPayConfig | null): FeexPayNetworkOption[] {
  const stored = Array.isArray(config?.networks)
    ? config!.networks!.filter((o) => o && o.country && o.slug)
    : [];

  if (stored.length > 0) {
    return stored.map((o) => ({
      country: o.country,
      countryLabel: getFeexPayCountry(o.country)?.label || o.country,
      slug: o.slug,
      label: o.label || o.slug,
      enabled: o.enabled !== false,
      fromCatalog: false,
    }));
  }

  const result: FeexPayNetworkOption[] = [];
  FEEXPAY_COUNTRIES.forEach((c) => {
    c.operators.forEach((op) => {
      result.push({
        country: c.code,
        countryLabel: c.label,
        slug: op.slug,
        label: op.label,
        enabled: true,
        fromCatalog: true,
      });
    });
  });
  return result;
}

/** Liste des pays disposant au moins d'un opérateur actif. */
export function getFeexPayActiveCountries(config?: FeexPayConfig | null): FeexPayCountry[] {
  const options = resolveFeexPayNetworks(config).filter((o) => o.enabled);
  const codes = new Set(options.map((o) => o.country));
  return FEEXPAY_COUNTRIES.filter((c) => codes.has(c.code));
}

/**
 * Normalise une URL d'API FeexPay.
 * L'hôte « sandbox-api-v2.feexpay.me » ne résout plus (DNS introuvable) :
 * toute URL est ramenée sur l'unique hôte exploitable, api-v2.feexpay.me.
 */
export function normalizeFeexPayApiUrl(apiUrl?: string | null): string {
  const value = (apiUrl || '').trim();
  if (!value) return FEEXPAY_DEFAULT_API_URL;
  return value
    .replace(/sandbox-api-v2\.feexpay\.me/gi, 'api-v2.feexpay.me')
    .replace(/sandbox\.feexpay\.me/gi, 'api-v2.feexpay.me')
    .replace(/\/+$/, '');
}

/** Construit l'URL v2 de « request to pay » pour un opérateur donné. */
export function buildFeexPayUrl(apiUrl: string | undefined, slug: string): string {
  const base = normalizeFeexPayApiUrl(apiUrl);
  const safeSlug = (slug || '').replace(/^\/+/, '');
  return `${base}/api/transactions/public/requesttopay/${safeSlug}`;
}

/**
 * URL de collecte par CARTE BANCAIRE (FeexPay « Card collection »).
 * Si le superadmin a renseigné une URL dédiée (`card_url`), elle fait foi ;
 * sinon on déduit l'endpoint standard v2 (« …/requesttopay/card »).
 */
export function buildFeexPayCardUrl(apiUrl: string | undefined, cardUrl?: string): string {
  const custom = (cardUrl || '').trim();
  if (custom) return normalizeFeexPayApiUrl(custom);
  const base = normalizeFeexPayApiUrl(apiUrl);
  return `${base}/api/transactions/public/requesttopay/card`;
}

/** URL d'API par défaut (un seul environnement FeexPay exploitable). */
export function defaultFeexPayApiUrl(_mode?: string): string {
  return FEEXPAY_DEFAULT_API_URL;
}

/**
 * Devise réellement encaissée par FeexPay pour un pays donné.
 * Afrique de l'Ouest → XOF, Afrique centrale (Congo…) → XAF.
 * Les deux monnaies sont à parité fixe (1 XAF = 1 XOF), donc la conversion
 * numérique ne change pas, mais l'affichage et le suivi doivent être exacts.
 */
export function getFeexPayGatewayCurrency(country?: string | null): string {
  return getFeexPayCountry(country)?.currency || FEEXPAY_CURRENCY;
}

/**
 * Identifiant de boutique FeexPay (« Shop ID »).
 *
 * ⚠️ FeexPay n'impose AUCUNE longueur : la documentation officielle utilise
 * elle-même un identifiant court du type « Ayg9lkjkhurIvNp ». On se contente
 * donc d'un contrôle de forme très souple (identifiant alphanumérique) et on
 * rejette uniquement la clé API (`fp_…`) qui n'a rien à faire ici.
 */
export function isValidFeexPayShopId(shop?: string | null): boolean {
  const value = (shop || '').trim();
  if (!value) return false;
  if (/^fp_/i.test(value)) return false;
  return /^[A-Za-z0-9_-]{4,80}$/.test(value);
}

/**
 * Normalise un numéro Mobile Money au format attendu par FeexPay
 * (indicatif pays + numéro, sans caractères spéciaux).
 */
export function normalizeFeexPayPhone(raw: string, callingCode: string): string {
  let digits = (raw || '').replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  const cc = (callingCode || '').replace(/\D/g, '');
  if (cc && !digits.startsWith(cc)) digits = `${cc}${digits}`;
  return digits;
}

/** Convertit un montant (devise du plan) en montant XOF pour FeexPay. */
export function computeFeexPayAmount(
  price: number,
  rate: number | string | undefined,
  currency?: string,
  rates?: Record<string, number | string>,
  targetCurrency?: string,
): number {
  const amount = Number(price) || 0;
  const code = (currency || '').toUpperCase();
  const target = (targetCurrency || FEEXPAY_CURRENCY).toUpperCase();
  const isCfa = (c: string) => c === 'XOF' || c === 'XAF';
  // XOF ↔ XAF : parité fixe, aucun taux à appliquer.
  if (currency && isCfa(code) && isCfa(target)) return Math.round(amount);
  const mapRate = Number(rates?.[code]);
  const safeRate = mapRate > 0 ? mapRate : Number(rate) > 0 ? Number(rate) : defaultXofRate(code);
  return Math.round(amount * safeRate);
}

/** Devises proposées dans l'éditeur de taux (1 unité → XOF). */
export const XOF_RATE_CURRENCIES: string[] = [
  'MAD', 'DHS', 'EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD', 'TND', 'DZD', 'EGP',
  'NGN', 'GHS', 'KES', 'ZAR', 'AED', 'SAR', 'QAR', 'TRY', 'CNY', 'INR', 'BRL',
  'MXN', 'XAF', 'GNF', 'CDF', 'MRU', 'JPY',
];

/**
 * Taux appliqué à une devise :
 * 1. taux de cette devise (`xof_rates`), 2. taux par défaut (`xof_rate`),
 * 3. taux de repli intégré. Renvoie toujours une valeur exploitable.
 */
export function resolveXofRateFor(
  currency: string | null | undefined,
  config?: FeexPayConfig | null,
): number {
  const code = (currency || 'XOF').toUpperCase();
  const mapRate = Number(config?.xof_rates?.[code]);
  if (mapRate > 0) return mapRate;
  const fallbackRate = Number(config?.xof_rate);
  if (fallbackRate > 0) return fallbackRate;
  return defaultXofRate(code);
}

/**
 * Taux de conversion par défaut vers le XOF (1 unité → XOF).
 * Sert de repli lorsque le SuperAdmin n'a pas renseigné de taux dans
 * les réglages FeexPay : sans cela, un abonnement à 50 DHS était envoyé
 * à FeexPay comme 50 XOF et refusé (montant sous le minimum).
 */
const DEFAULT_XOF_RATES: Record<string, number> = {
  XOF: 1,
  XAF: 1,
  MAD: 61,
  DHS: 61,
  EUR: 655.957,
  USD: 600,
  GBP: 780,
  CHF: 740,
  CAD: 440,
  AUD: 400,
  DZD: 4.5,
  TND: 195,
  EGP: 12,
  GNF: 0.07,
  CDF: 0.21,
  MRU: 15.3,
  AED: 163,
  SAR: 160,
  QAR: 165,
  TRY: 17,
  NGN: 0.4,
  GHS: 40,
  KES: 4.6,
  ZAR: 33,
  JPY: 4,
  CNY: 83,
  INR: 7.2,
  BRL: 110,
  MXN: 31,
};

/** Taux de repli vers le XOF pour une devise donnée. */
export function defaultXofRate(currency?: string | null): number {
  const code = (currency || 'XOF').toUpperCase();
  const rate = DEFAULT_XOF_RATES[code];
  return Number(rate) > 0 ? rate : 1;
}

/** Vérifie que le montant XOF est dans les bornes FeexPay. */
export function isFeexPayAmountValid(amount: number): boolean {
  return Number.isFinite(amount) && amount >= FEEXPAY_MIN_AMOUNT && amount <= FEEXPAY_MAX_AMOUNT;
}